import { supabase, supabaseUrl, supabaseAnonKey } from './supabaseService';
import { resendService } from './resendService';
import type { Contract, ContractSignature, ContractPaymentTerms } from '../types/contract';
import type { ProposalContent } from '../types/proposal-blocks';

// ─── Helpers ──────────────────────────────────────────────────

function generateSecureToken(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

function mapRow(row: any): Contract {
  return {
    id: row.id,
    contract_number: row.contract_number,
    proposal_id: row.proposal_id,
    status: row.status,
    token: row.token,
    client_data: row.client_data || {},
    payment_terms: row.payment_terms,
    total_amount: parseFloat(row.total_amount),
    signed_pdf_url: row.signed_pdf_url,
    created_at: row.created_at,
    sent_at: row.sent_at,
    signed_at: row.signed_at,
    expires_at: row.expires_at,
    created_by: row.created_by,
  };
}

// ─── Service ──────────────────────────────────────────────────

class ContractService {
  /**
   * Calculate total amount from proposal content (milestones or packages).
   */
  private calculateTotalFromContent(content: ProposalContent): number {
    // Try milestones first
    const milestonesBlock = content.blocks.find((b) => b.type === 'milestones') as any;
    if (milestonesBlock?.items) {
      const total = milestonesBlock.items.reduce((sum: number, item: any) => {
        const amount = parseFloat((item.amount || '0').replace(/[^\d.,]/g, '').replace(',', '.'));
        return sum + (isNaN(amount) ? 0 : amount);
      }, 0);
      if (total > 0) return total;
    }

    // Fallback: try package_page prices
    const packages = content.blocks.filter((b) => b.type === 'package_page') as any[];
    if (packages.length > 0) {
      const total = packages.reduce((sum: number, pkg: any) => {
        const price = parseFloat((pkg.price || '0').replace(/[^\d.,]/g, '').replace(',', '.'));
        return sum + (isNaN(price) ? 0 : price);
      }, 0);
      if (total > 0) return total;
    }

    return 0;
  }

  /**
   * Create a new contract linked to a proposal.
   */
  async createContract(
    proposalId: string,
    content: ProposalContent,
    totalAmountOverride?: number
  ): Promise<Contract> {
    const { data: user } = await supabase.auth.getUser();

    const totalAmount = totalAmountOverride || this.calculateTotalFromContent(content);
    if (totalAmount <= 0) {
      throw new Error('El importe total del contrato es 0. Verifica que la propuesta tenga importes.');
    }

    const paymentTerms: ContractPaymentTerms = {
      first_payment: Math.round(totalAmount * 0.4),
      second_payment: Math.round(totalAmount * 0.3),
      third_payment: Math.round(totalAmount * 0.3),
      description: '40% inicio, 30% fase intermedia, 30% entrega final',
    };

    const token = generateSecureToken();

    const { data: contractNumber, error: numberError } = await supabase.rpc('generate_contract_number');
    if (numberError) throw new Error(`Error generando numero de contrato: ${numberError.message}`);

    const { data, error } = await supabase
      .from('contracts')
      .insert({
        contract_number: contractNumber,
        proposal_id: proposalId,
        status: 'pending',
        token,
        payment_terms: paymentTerms,
        total_amount: totalAmount,
        created_by: user.user?.id || null,
      })
      .select()
      .single();

    if (error) throw new Error(`Error creando contrato: ${error.message}`);
    return mapRow(data);
  }

  /**
   * Get contract by its public token (no auth needed).
   *
   * Antes se leía la tabla contracts directamente con una policy anon
   * USING(true), lo que permitía enumerar todos los contratos. Ahora se
   * pasa por la RPC get_contract_by_token que devuelve solo los campos
   * necesarios y filtra server-side.
   */
  async getContractByToken(token: string): Promise<Contract & { prefill?: any; signature?: any } | null> {
    const { data, error } = await supabase.rpc('get_contract_by_token', { p_token: token });
    if (error) throw new Error(`Error buscando contrato: ${error.message}`);
    const row = Array.isArray(data) ? data[0] : data;
    if (!row) return null;

    const contract = mapRow({
      id: row.id,
      status: row.status,
      client_data: row.client_data,
      payment_terms: row.payment_terms,
      total_amount: row.total_amount,
      sent_at: row.sent_at,
      signed_at: row.signed_at,
      expires_at: row.expires_at,
      token,
      contract_number: '',
      proposal_id: null,
      signed_pdf_url: null,
      created_at: null,
      created_by: null,
    });

    // Extendemos el resultado con prefill (datos del lead/contact linkeados)
    // y signature (si el contrato ya está firmado), evitando reads directos
    // a leads/contacts/contact_signatures que ahora son admin-only.
    return {
      ...contract,
      prefill: row.prefill || null,
      signature: row.signature || null,
      proposalTitle: row.proposal_title || null,
    } as any;
  }

  /**
   * Get all contracts for a proposal.
   */
  async getContractsByProposal(proposalId: string): Promise<Contract[]> {
    const { data, error } = await supabase
      .from('contracts')
      .select('*')
      .eq('proposal_id', proposalId)
      .order('created_at', { ascending: false });

    if (error) throw new Error(`Error buscando contratos: ${error.message}`);
    return (data || []).map(mapRow);
  }

  /**
   * Mark contract as sent and record timestamp.
   */
  async markAsSent(contractId: string): Promise<void> {
    const { error } = await supabase
      .from('contracts')
      .update({ status: 'sent', sent_at: new Date().toISOString() })
      .eq('id', contractId);

    if (error) throw new Error(`Error actualizando contrato: ${error.message}`);
  }

  /**
   * Submit the client's signature and complete the contract.
   */
  async submitSignature(
    token: string,
    signatureData: string,
    clientInfo: {
      name: string;
      email: string;
      company?: string;
      nif: string;
      address: string;
      phone?: string;
      position?: string;
    }
  ): Promise<{ contractId: string }> {
    const ipAddress = await fetch('https://api.ipify.org?format=json')
      .then((res) => res.json())
      .then((d) => d.ip)
      .catch(() => 'unknown');

    const userAgent = navigator.userAgent;

    const { data, error } = await supabase.rpc('complete_contract_signature', {
      p_contract_token: token,
      p_signature_data: signatureData,
      p_client_name: clientInfo.name,
      p_client_email: clientInfo.email,
      p_client_company: clientInfo.company || null,
      p_client_nif: clientInfo.nif,
      p_client_address: clientInfo.address,
      p_client_phone: clientInfo.phone || null,
      p_client_position: clientInfo.position || null,
      p_ip_address: ipAddress,
      p_user_agent: userAgent,
    });

    if (error) throw new Error(`Error al firmar: ${error.message}`);
    if (!data?.success) throw new Error(data?.error || 'Error desconocido al firmar');

    return { contractId: data.contract_id };
  }

  /**
   * Get the signature record for a signed contract.
   */
  async getSignature(contractId: string): Promise<ContractSignature | null> {
    const { data, error } = await supabase
      .from('contract_signatures')
      .select('*')
      .eq('contract_id', contractId)
      .maybeSingle();

    if (error) throw new Error(`Error buscando firma: ${error.message}`);
    return data as ContractSignature | null;
  }

  /**
   * Send the contract link email to the client.
   */
  async sendContractEmail(
    contract: Contract,
    clientEmail: string,
    clientName: string,
    projectTitle: string
  ): Promise<void> {
    const publicUrl = `${window.location.origin}/contrato/${contract.token}`;

    // Load config desde BD (localStorage fallaba en incógnito/dispositivo
    // nuevo). Mismo patrón fixado en proposalService y meetings hoy.
    const config = await resendService.loadConfigFromDatabase();
    if (!config?.isConfigured || !config.apiKey) {
      throw new Error('Resend no esta configurado. Ve a Configuracion > Email.');
    }

    await resendService.sendEmail({
      from: `${config.fromName || 'Cero Ideas'} <${config.fromEmail || 'hola@ceroideas.es'}>`,
      to: clientEmail,
      subject: `Contrato para firma: ${projectTitle}`,
      html: this.buildContractEmailHtml(contract.contract_number, clientName, publicUrl, projectTitle, contract.total_amount),
      tags: [
        { name: 'type', value: 'contract' },
        { name: 'contract_id', value: contract.id },
      ],
    });

    await this.markAsSent(contract.id);
  }

  /**
   * Send post-signature notification emails.
   */
  async sendSignedNotification(
    contract: Contract,
    clientName: string,
    clientEmail: string,
    projectTitle: string
  ): Promise<void> {
    try {
      await supabase.functions.invoke('contract-signed-notification', {
        body: {
          contract_number: contract.contract_number,
          contract_token: contract.token,
          project_title: projectTitle,
          client_name: clientName,
          client_email: clientEmail,
          total_amount: contract.total_amount,
          signed_at: new Date().toISOString(),
        },
      });
    } catch {
      // Non-blocking — signature is already saved
      console.warn('Email notification failed, but signature was successful');
    }
  }

  // ─── Private ──────────────────────────────────────────────

  private buildContractEmailHtml(
    contractNumber: string,
    clientName: string,
    publicUrl: string,
    projectTitle: string,
    totalAmount: number
  ): string {
    const formattedAmount = totalAmount.toLocaleString('es-ES', { minimumFractionDigits: 2 });
    return `
<!DOCTYPE html>
<html>
<head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1.0"></head>
<body style="margin:0;padding:0;background:#0A0A0A;font-family:'Inter',system-ui,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background:#0A0A0A;padding:40px 20px;">
    <tr><td align="center">
      <table width="600" cellpadding="0" cellspacing="0" style="background:#111111;border-radius:12px;border:1px solid rgba(255,255,255,0.08);overflow:hidden;">
        <tr><td style="padding:32px 40px 24px;border-bottom:1px solid rgba(255,255,255,0.08);">
          <table width="100%" cellpadding="0" cellspacing="0">
            <tr>
              <td>
                <div style="display:inline-block;width:32px;height:32px;background:#B6FF3C;border-radius:6px;text-align:center;line-height:32px;font-weight:700;font-size:16px;color:#0A0A0A;">C</div>
                <span style="margin-left:10px;font-size:14px;font-weight:600;color:#F0F0F0;">Cero Ideas</span>
              </td>
            </tr>
          </table>
        </td></tr>
        <tr><td style="padding:40px;">
          <p style="font-size:14px;color:#A0A0A0;margin:0 0 8px;">Hola ${clientName},</p>
          <h1 style="font-size:24px;font-weight:700;color:#F0F0F0;margin:0 0 8px;letter-spacing:-0.02em;">Contrato ${contractNumber}</h1>
          <p style="font-size:13px;color:#666666;font-family:'JetBrains Mono',monospace;margin:0 0 8px;">${projectTitle}</p>
          <p style="font-size:13px;color:#666666;font-family:'JetBrains Mono',monospace;margin:0 0 24px;">Importe: ${formattedAmount} EUR + IVA</p>
          <p style="font-size:14px;color:#A0A0A0;line-height:1.6;margin:0 0 32px;">
            Hemos preparado el contrato para tu proyecto. Revisalo y firmalo digitalmente en el siguiente enlace:
          </p>
          <table cellpadding="0" cellspacing="0" style="margin:0 0 32px;">
            <tr><td style="background:#B6FF3C;border-radius:8px;">
              <a href="${publicUrl}" style="display:inline-block;padding:12px 28px;font-size:14px;font-weight:600;color:#0A0A0A;text-decoration:none;">
                Revisar y firmar contrato
              </a>
            </td></tr>
          </table>
          <p style="font-size:12px;color:#666666;margin:0;word-break:break-all;">
            O copia este enlace: <a href="${publicUrl}" style="color:#B6FF3C;">${publicUrl}</a>
          </p>
          <p style="font-size:12px;color:#666666;margin:16px 0 0;">
            Este enlace es valido durante 30 dias. Si tienes dudas, responde directamente a este correo.
          </p>
        </td></tr>
        <tr><td style="padding:24px 40px;border-top:1px solid rgba(255,255,255,0.08);">
          <p style="font-size:11px;color:#666666;margin:0;text-align:center;">
            Este email ha sido enviado desde Cero Ideas CRM.
          </p>
        </td></tr>
      </table>
    </td></tr>
  </table>
</body>
</html>`;
  }
}

export const contractService = new ContractService();
